<template>
  <section class="history-stats statistics">
    <Breadcrumbs
      :breadcrumbs="[
        { title: $t('menu.statistics'), disabled: true },
        { title: $t('menu.historystats'), disabled: true }
      ]">
    </Breadcrumbs>

    <form class="evollu-form">
      <div class="row" v-if="clusters.length">
        <div class="col-12 col-sm-5">
          <va-switch v-model="filters.showEntityStats">
            <span slot="trueTitle">{{$t('kiosks.entity')}}</span>
            <span slot="falseTitle">
              <span>
                {{$t('statistics.cluster')}}
              </span>
              <span v-if="!filters.showEntityStats && filters.cluster" @click.prevent="expandCluster = !expandCluster" class="cluster-dropdown">
                <i class="fa" :class="{'fa-chevron-up': expandCluster, 'fa-chevron-down': !expandCluster}"></i>
              </span>
            </span>
          </va-switch>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-sm-5">
          <span v-if="filters.showEntityStats">
            <label class="form-label">{{$t('statistics.select_entity')}}:</label>
            <multiselect
              id="entities-multiselect"
              v-model="filters.entity"
              :options="entityOptions"
              track-by="id"
              :custom-label="formatedEntityLabel"
              :placeholder="!entityOptions.length ? $t('statistics.no_entities') : $t('statistics.totalValues')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="entityOptions.length < 2"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props" v-if="entityOptions.length > 1 && filters.entity !== null">
                <div id="entities-multiselect-clear" class="multiselect__clear" @mousedown.prevent.stop="filters.entity = null"></div>
              </template>
            </multiselect>
          </span>

          <span v-else>
            <label class="form-label">{{$t('statistics.select_cluster')}}:</label>
            <multiselect
              id="clusters-multiselect"
              v-model="filters.cluster"
              :options="clusterOptions"
              track-by="id"
              :custom-label="formatedEntityLabel"
              :placeholder="!clusterOptions.length ? $t('statistics.no_clusters') : $t('statistics.totalValues')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="clusterOptions.length < 2"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props" v-if="clusterOptions.length > 1 && filters.cluster !== null">
                <div id="clusters-multiselect-clear" class="multiselect__clear" @mousedown.prevent.stop="filters.cluster = null"></div>
              </template>
            </multiselect>
          </span>
        </div>

        <div class="col-12 col-sm-4">
          <RangeDatePicker
            id="range-date-picker"
            :label="`${$t('statistics.date_interval')}:`"
            v-model="filters.dateRange"
            placeholder="---"
            :clearable="false"
            :disabled="isLoading"
          >
          </RangeDatePicker>
        </div>

        <div class="col-12 col-sm-3">
          <label class="form-label">{{$t('statistics.view_options')}}:</label>
          <multiselect
            v-model="filters.viewOption"
            :options="viewOptions"
            select-label=""
            deselect-label="×"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            :disabled="isLoading"
          >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
            <template slot="singleLabel" slot-scope="props">
              <span class="option__name">{{ $t(props.option.name) }}</span>
            </template>
            <template slot="option" slot-scope="props">
              <span class="option__name">{{ $t(props.option.name) }}</span>
            </template>
          </multiselect>
        </div>

      </div>

      <div v-if="!filters.showEntityStats && filters.cluster && expandCluster" class="row mb-5">
        <div class="col-12">
          <va-badge outline v-for="(name, index) in filters.cluster.entity_names" :key="index">
            {{name}}
          </va-badge>
        </div>
      </div>
    </form>

    <div v-show="isLoading" class="no-data-wrapper">
      <h6><b>{{$t('tables.loading')}}</b></h6>
      <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
    </div>

    <div v-show="!isLoading && !entityOptions.length" class="no-data-wrapper">
      <span class="is-bold">{{$t('pyramid.no_data')}}</span>
    </div>

    <div v-show="!isLoading && entityOptions.length">
      <history-chart :data="chartData" :viewOption="filters.viewOption.value"></history-chart>
    </div>
  </section>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapGetters } from 'vuex'
import HistoryChart from './HistoryChart'
import { StatisticsService } from '@/api/LeadsService'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import { HISTORY_STATS } from '@/local-storage/LocalStorageKeys'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'

export default {
  components: {
    Breadcrumbs,
    Multiselect,
    HistoryChart,
    RangeDatePicker,
    LoadingAnimation
  },
  mixins: [
    MultiselectLabelFormatter
  ],
  data () {
    return {
      viewOptions: [
        { 'name': 'historystats.by_day', 'value': 'day' },
        { 'name': 'historystats.by_week', 'value': 'week' },
        { 'name': 'historystats.by_month', 'value': 'month' }
      ],
      expandCluster: false,
      isLoading: false,
      dataset: {},
      totalValues: null,
      filters: {
        showEntityStats: true,
        viewOption: { 'name': 'historystats.by_day', 'value': 'day' },
        entity: null,
        cluster: null,
        dateRange: {
          start: this.moment().subtract(1, 'months').format('YYYY-MM-DD'),
          end: this.moment().format('YYYY-MM-DD')
        },
        dateType: 'created_at'
      },
      dateOptions: [
        'created_at',
        'updated_at',
        'qualified_at',
        'sale_at'
      ]
    }
  },
  methods: {
    loadBaseData () {
      this.isLoading = true
      StatisticsService.getFullStatsPerDay(this.requestParams)
        .then(originalData => { this.createDataset(originalData) })
        .catch((error) => {
          console.log(error)
          this.showToast(this.$t('statistics.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    createDataset: function (data) {
      this.dataset = {}
      this.totalValues = null

      if (data.length) {
        data.forEach(element => {
          this.dataset[element.id] = this.createDatasetEntry(element)
        })

        this.totalValues = this.calculateAggregateData(Object.keys(this.dataset), this.filters.dateRange.start, this.filters.dateRange.end)
      }
    },
    createDatasetEntry: function (data = {}) {
      return {
        stats: {},
        ...data
      }
    },
    createEntryRecord: function (data = {}) {
      return {
        appointment_ent: 0,
        appointment_missed: 0,
        appointment_not_applicable: 0,
        appointment_no_sale: 0,
        appointment_possible_sale: 0,
        cancelled: 0,
        cancelled_ht: 0,
        cancelled_ht_loss: 0,
        cancelled_ht_no_loss: 0,
        contacts_no_success: 0,
        contacts_no_success_hl: 0,
        contacts_postponed: 0,
        contacts_success: 0,
        contacts_success_hl: 0,
        converted_to_appointment: 0,
        counter: 0,
        kiosk: 0,
        app: 0,
        web: 0,
        kiosk_ht: 0,
        kiosk_ht_loss: 0,
        qualified: 0,
        trial: 0,
        sale: 0,
        sessions: 0,
        ...data
      }
    },
    calculateAggregateData: function (entityIds, start, end) {
      let aggregate = this.createDatasetEntry()
      let currentDate = this.moment(start)
      const endDate = this.moment(end)

      const keys = Object.keys(this.createEntryRecord())

      for (let date = currentDate; date.isSameOrBefore(endDate); date.add(1, 'days')) {
        let dateStr = date.format('YYYY-MM-DD')
        aggregate.stats[dateStr] = this.createEntryRecord()

        entityIds.forEach(entityId => {
          if (this.dataset.hasOwnProperty(entityId)) {
            if (this.dataset[entityId].stats.hasOwnProperty(dateStr)) {
              keys.forEach(key => {
                if (this.dataset[entityId].stats[dateStr].hasOwnProperty(key)) {
                  aggregate.stats[dateStr][key] += this.dataset[entityId].stats[dateStr][key]
                }
              })
            }
          }
        })
      }

      return aggregate
    },
    getFullEntityById: function (id) {
      return this.fullEntities.find(e => e.id === id)
    },
    loadLocalStorage: function () {
      return new Promise((resolve, reject) => {
        const state = LocalStorageHelper.get(HISTORY_STATS)
        if (state !== null) {
          this.filters = {
            ...this.fitlers,
            ...state
          }
        }
        resolve()
      })
    }
  },
  computed: {
    ...mapGetters([
      'clusters'
    ]),
    audiologyService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollead_server)
    },
    fullEntities: function () {
      return this.audiologyService ? this.audiologyService.entities : []
    },
    requestParams: function () {
      return {
        from: this.filters.dateRange.start,
        until: this.filters.dateRange.end,
        date: this.filters.dateType
      }
    },
    entityOptions: function () {
      return Object.values(this.dataset)
        .map(element => {
          let entity = this.getFullEntityById(element.id)
          return {
            id: element.id,
            name: element.name,
            cities: entity ? entity.cities : []
          }
        })
    },
    clusterOptions: function () {
      return this.clusters
        .map(cluster => {
          let entitiesWithPermissions = this.entityOptions.filter(entity => cluster.entitiesIds.some(entityId => entity.id === entityId))
          cluster.entitiesWithPermissions = entitiesWithPermissions.length
          cluster.entity_names = entitiesWithPermissions.map(e => e.name)
          return cluster
        })
        .filter(cluster => cluster.entitiesWithPermissions > 0)
    },
    chartData: function () {
      let values = null

      if (Object.values(this.dataset).length) {
        if (this.filters.showEntityStats && this.filters.entity) {
          values = this.dataset[this.filters.entity.id].stats
        } else if (!this.filters.showEntityStats && this.filters.cluster) {
          values = this.calculateAggregateData(this.filters.cluster.entitiesIds, this.filters.dateRange.start, this.filters.dateRange.end).stats
        } else if (this.totalValues) {
          values = this.totalValues.stats
        }
      }

      return { values, from: this.filters.dateRange.start, until: this.filters.dateRange.end }
    }
  },
  watch: {
    entityOptions: {
      handler: function (options) {
        if (options.length === 1) {
          this.filters.entity = options[0]
        } else if (this.filters.entity && !options.some(option => option.id === this.filters.entity.id)) {
          this.filters.entity = null
        }
      },
      immediate: true
    },
    clusterOptions: {
      handler: function (options) {
        // Multiselect did not update label unless v-model was reset to null and then set again
        const previousValue = this.filters.cluster
        this.filters.cluster = null
        let newSelectedValue = null

        if (options.length === 1) {
          newSelectedValue = options[0]
        } else if (previousValue) {
          newSelectedValue = options.find(option => option.id === previousValue.id) || null
        }

        this.$nextTick(() => {
          this.filters.cluster = newSelectedValue
        })
      },
      immediate: true
    },
    filters: {
      handler: function () {
        LocalStorageHelper.set(HISTORY_STATS, this.filters)
      },
      deep: true
    },
    'filters.showEntityStats': function (value) {
      if (!value && !this.clusters.length) {
        this.filters.showEntityStats = true
      }
    },
    requestParams: function (value) {
      this.loadBaseData()
    }
  },
  mounted () {
    this.loadLocalStorage()
      .then(() => {
        this.loadBaseData()
      })
  }
}
</script>

<style lang="scss">

.cluster-dropdown {
  position: absolute;
  right: 30px;
}

.stats-number {
  line-height: 1;
}

.final-status .widget {
  background: #d8dcdc;
}

.cancelled-status .widget {
  background: #d8dcdc;
  color: #7d8888;
}

.final-status-success .widget {
  background: #5dceb3;
}

.no-data-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 25vh;
}
</style>
